import React, { useCallback, useContext, useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  CheckCircle2,
  Clock,
  ExternalLink,
} from "lucide-react";

import { MainStore } from "../../stores/Main";
import API from "../../utils/api";
import history from "../../utils/history";

const DisclosurePage = () => {
  const [profile, setProfile] = useState({});
  const [disclosures, setDisclosures] = useState({
    set: false,
    data: {},
    url: "",
    loaded: false,
  });
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isCrossOrigin, setIsCrossOrigin] = useState(false);
  const iframeRef = React.useRef(null);
  const disclosureContentRef = React.useRef(null);

  const {
    appConfig,
    questionnaire,
    sentimentQuestionnaire,
    handleAcceptDisc,
    userType,
  } = useContext(MainStore);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await API.get("/profile");
      if (response.data) {
        setProfile(response.data);
      }
    } catch (e) {}
  }, []);

  const fetchDisclosures = async () => {
    try {
      const response = await API.get("/disclosures");
      if (!response.data.result_code) {
        if (response.data.response) {
          response.data.response.sort((a, b) => {
            if (a.Data.NoticesResponse.order < b.Data.NoticesResponse.order)
              return -1;
            if (a.Data.NoticesResponse.order > b.Data.NoticesResponse.order)
              return 1;
            return 0;
          });
          setDisclosures({ set: true, data: response.data, loaded: true });
        } else if (response.data.external_disclosure_url) {
          setDisclosures({
            set: true,
            data: {},
            url: response.data.external_disclosure_url,
          });
        }
      }
    } catch (e) {}
  };

  const accept = useCallback(async () => {
    setIsAccepting(true);
    try {
      const response = await API.post("/disclosures/accept");
      if (!response.data.response_code) {
        handleAcceptDisc();
        setTimeout(() => {
          if (userType === "consumer-quick-access") {
            history.push("/payment-options");
          } else if (
            !questionnaire &&
            appConfig.onboarding_financials !== "disabled"
          ) {
            history.push("/questionnaire");
          } else if (
            !sentimentQuestionnaire &&
            appConfig.onboarding_sentiment !== "disabled"
          ) {
            history.push("/sentiment-questionnaire");
          } else {
            history.push("/");
          }
        }, 500);
      }
    } catch (e) {
      setIsAccepting(false);
    }
  }, [
    appConfig,
    sentimentQuestionnaire,
    questionnaire,
    handleAcceptDisc,
    userType,
  ]);

  React.useEffect(() => {
    if (profile.state !== "") {
      fetchDisclosures().then();
    }
  }, [profile.state]);

  React.useEffect(() => {
    fetchProfile().then();
  }, []);

  // Check if content is scrollable when disclosures are loaded
  React.useEffect(() => {
    if (disclosures.data.response && disclosures.data.response.length > 0) {
      // Reset when new disclosures are loaded
      setHasScrolledToBottom(false);

      // Check if content is scrollable after a brief delay to ensure DOM is updated
      setTimeout(() => {
        if (disclosureContentRef.current) {
          const element = disclosureContentRef.current;
          const isScrollable = element.scrollHeight > element.clientHeight;

          if (!isScrollable) {
            // Content fits without scrolling, enable the button
            setHasScrolledToBottom(true);
          } else {
            // Check if already at bottom (in case content is very short)
            const scrollTop = element.scrollTop;
            const scrollHeight = element.scrollHeight;
            const clientHeight = element.clientHeight;

            if (scrollHeight - scrollTop - clientHeight < 50) {
              setHasScrolledToBottom(true);
            }
          }
        }
      }, 100);
    }
  }, [disclosures.data.response]);

  // Fallback timer for URL-based disclosures to ensure button eventually enables
  React.useEffect(() => {
    if (disclosures.url && disclosures.set) {
      // Set a maximum wait time before enabling the button
      const fallbackTimer = setTimeout(() => {
        if (!hasScrolledToBottom) {
          console.log("Fallback timer activated - enabling accept button");
          setHasScrolledToBottom(true);
        }
      }, 5000); // 5 seconds maximum wait

      return () => clearTimeout(fallbackTimer);
    }
  }, [disclosures.url, disclosures.set, hasScrolledToBottom]);

  return (
    <div className="min-h-screen p-4 md:p-8 relative overflow-x-hidden">
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 lg:p-12"
        >
          {disclosures.set && (
            <>
              <div className="mb-6 sm:mb-8 md:mb-10">
                <div className="flex items-center justify-center gap-3 mb-4 sm:flex-col sm:gap-4 sm:text-center">
                  <div className="flex items-center justify-center w-12 h-12 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-800 to-blue-600 rounded-xl sm:rounded-2xl shadow-lg">
                    <ShieldCheck size={28} className="text-white sm:hidden" />
                    <ShieldCheck
                      size={48}
                      className="text-white hidden sm:block"
                    />
                  </div>
                  <h1 className="text-xl sm:text-3xl md:text-4xl font-bold text-gray-900">
                    Consumer Rights &<br className="sm:hidden" />
                    <span className="sm:inline"> </span>Disclosures
                  </h1>
                </div>
                <p className="text-sm sm:text-lg text-gray-600 px-2 sm:px-0 text-center">
                  Please carefully review the disclosures below before
                  proceeding
                </p>
              </div>

              {disclosures.data.response && (
                <div className="mb-8">
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <div
                      ref={disclosureContentRef}
                      className="max-h-[400px] sm:max-h-[500px] overflow-y-auto p-3 sm:p-6 bg-white"
                      onScroll={(e) => {
                        const element = e.target;
                        const scrollTop = element.scrollTop;
                        const scrollHeight = element.scrollHeight;
                        const clientHeight = element.clientHeight;

                        // Check if user has scrolled to within 50px of the bottom
                        if (scrollHeight - scrollTop - clientHeight < 50) {
                          setHasScrolledToBottom(true);
                        }
                      }}
                    >
                      {disclosures.data.response.map((disc, index) => (
                        <div
                          key={index}
                          className="prose prose-gray max-w-none mb-6 last:mb-0"
                          dangerouslySetInnerHTML={{
                            __html: disc.Data.NoticesResponse.text,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {disclosures.data.updated && (
                    <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                      <Clock size={16} />
                      <span>
                        Last Updated:{" "}
                        {new Date(
                          disclosures.data.updated.seconds * 1000
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {disclosures.url && (
                <div className="mb-8">
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <iframe
                      ref={iframeRef}
                      onLoad={() => {
                        setDisclosures({ ...disclosures, loaded: true });
                        // Add scroll listener to iframe content
                        try {
                          const iframeDoc =
                            iframeRef.current?.contentDocument ||
                            iframeRef.current?.contentWindow?.document;
                          if (iframeDoc) {
                            const checkScroll = () => {
                              const scrollElement =
                                iframeDoc.documentElement || iframeDoc.body;
                              const scrollTop = scrollElement.scrollTop;
                              const scrollHeight = scrollElement.scrollHeight;
                              const clientHeight = scrollElement.clientHeight;

                              // Check if user has scrolled to within 50px of the bottom
                              if (
                                scrollHeight - scrollTop - clientHeight <
                                50
                              ) {
                                setHasScrolledToBottom(true);
                              }
                            };

                            // Check initial scroll position
                            checkScroll();

                            // Add scroll listener
                            iframeDoc.addEventListener("scroll", checkScroll);
                          }
                        } catch (e) {
                          // Cross-origin iframe - can't access content
                          console.log(
                            "Cannot access iframe content - cross-origin restrictions"
                          );
                          setIsCrossOrigin(true);
                          // For cross-origin iframes, enable after a reasonable reading time
                          // This ensures users can still proceed even without scroll detection
                          setTimeout(() => {
                            setHasScrolledToBottom(true);
                          }, 3000); // Enable after 3 seconds
                        }
                      }}
                      onError={() => {
                        // Handle iframe loading errors
                        console.log("Error loading iframe");
                        setDisclosures({ ...disclosures, loaded: true });
                        setIsCrossOrigin(true);
                        // Enable button after delay on error
                        setTimeout(() => {
                          setHasScrolledToBottom(true);
                        }, 3000); // Enable after 3 seconds on error
                      }}
                      className="w-full min-h-[250px] sm:min-h-[400px] md:min-h-[500px] bg-white"
                      src={disclosures.url}
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t-2 border-gray-200">
                <div className="flex justify-center">
                  <button
                    className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 bg-gradient-to-r from-blue-800 to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed sm:min-w-[200px] text-sm sm:text-base"
                    disabled={
                      (!disclosures.loaded && disclosures.url) ||
                      isAccepting ||
                      !hasScrolledToBottom
                    }
                    onClick={() => accept()}
                    title={
                      !hasScrolledToBottom
                        ? "Please scroll to the bottom of the disclosures to continue"
                        : ""
                    }
                  >
                    {isAccepting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={18} className="sm:hidden" />
                        <CheckCircle2 size={20} className="hidden sm:block" />
                        <span>Continue</span>
                      </>
                    )}
                  </button>
                </div>
                {!hasScrolledToBottom && (
                  <div className="text-center text-sm text-gray-600 mt-4">
                    {isCrossOrigin
                      ? "Please review the disclosures above to continue."
                      : "Please scroll to the bottom of the disclosures to continue"}
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DisclosurePage;
